import { HttpErrorResponse } from '@angular/common/http';

import { createAction, props } from '@ngrx/store';

import { NetSheetSplitOptionsEnum, NetSheetStateEnum } from '../../enums/net-sheet';
import {
  NetSheetCustomerInformation,
  NetSheetFormMiscItem,
  NetSheetMortgage,
  NetSheetNameValuePair,
  NetSheetResult,
  NetSheetState,
  NetSheetTaxesHomeownersDuesTerm,
  NetSheetTaxesTerm
} from '../../typings/net-sheet';

export const loadInitialData = createAction('[Net Sheet] Load Initial Data');

export const loadTransactionInformationLocationStates = createAction(
  '[Net Sheet] Load Transaction Information Location States',
  props<{
    payload: {
      isEscrow: boolean;
    };
  }>()
);

export const loadTransactionInformationLocationStatesSuccess = createAction(
  '[Net Sheet] Load Transaction Information Location States Success',
  props<{
    payload: {
      isEscrow: boolean;
      states: NetSheetState[];
    };
  }>()
);

export const loadTransactionInformationLocationStatesFailure = createAction(
  '[Net Sheet] Load Transaction Information Location States Failure',
  props<{
    payload: {
      error: HttpErrorResponse;
    };
  }>()
);

export const loadTransactionInformationLocationCounties = createAction(
  '[Net Sheet] Load Transaction Information Location Counties',
  props<{
    payload: {
      isEscrow: boolean;
      state: NetSheetStateEnum;
    };
  }>()
);

export const loadTransactionInformationLocationCountiesSuccess = createAction(
  '[Net Sheet] Load Transaction Information Location Counties Success',
  props<{
    payload: {
      counties: NetSheetNameValuePair[];
      isEscrow?: boolean;
    };
  }>()
);

export const loadTransactionInformationLocationCountiesFailure = createAction(
  '[Net Sheet] Load Transaction Information Location Counties Failure',
  props<{
    payload: {
      error: HttpErrorResponse;
    };
  }>()
);

export const loadTransactionInformationLocationCities = createAction(
  '[Net Sheet] Load Transaction Information Location Cities',
  props<{
    payload: {
      county: string;
      isEscrow: boolean;
      state: NetSheetStateEnum;
    };
  }>()
);

export const loadTransactionInformationLocationCitiesSuccess = createAction(
  '[Net Sheet] Load Transaction Information Location Cities Success',
  props<{
    payload: {
      cities: NetSheetNameValuePair[];
      isEscrow?: boolean;
    };
  }>()
);

export const loadTransactionInformationLocationCitiesFailure = createAction(
  '[Net Sheet] Load Transaction Information Location Cities Failure',
  props<{
    payload: {
      error: HttpErrorResponse;
    };
  }>()
);

export const loadTransactionInformationLocationZips = createAction(
  '[Net Sheet] Load Transaction Information Location Zips',
  props<{
    payload: {
      city: string;
      county: string;
      state: NetSheetStateEnum;
    };
  }>()
);

export const loadTransactionInformationLocationZipsSuccess = createAction(
  '[Net Sheet] Load Transaction Information Location Zips Success',
  props<{
    payload: {
      zips: NetSheetNameValuePair[];
    };
  }>()
);

export const loadTransactionInformationLocationZipsFailure = createAction(
  '[Net Sheet] Load Transaction Information Location Zips Failure',
  props<{
    payload: {
      error: HttpErrorResponse;
    };
  }>()
);

export const loadTransactionInformationLocationEscrowBranches = createAction(
  '[Net Sheet] Load Transaction Information Location Escrow Branches',
  props<{
    payload: {
      county: string;
      state: NetSheetStateEnum;
    };
  }>()
);

export const loadTransactionInformationLocationEscrowBranchesSuccess = createAction(
  '[Net Sheet] Load Transaction Information Location Escrow Branches Success',
  props<{
    payload: {
      branches: NetSheetNameValuePair[];
    };
  }>()
);

export const loadTransactionInformationLocationEscrowBranchesFailure = createAction(
  '[Net Sheet] Load Transaction Information Location Escrow Branches Failure',
  props<{
    payload: {
      error: HttpErrorResponse;
    };
  }>()
);

export const loadPropertyProfilePrefillInformation = createAction(
  '[Net Sheet] Load Property Profile Prefill Information',
  props<{
    payload: {
      id: string;
    };
  }>()
);

export const loadPropertyProfilePrefillInformationSuccess = createAction(
  '[Net Sheet] Load Property Profile Prefill Information Success',
  props<{
    payload: {
      address?: string;
      city?: string;
      county?: string;
      firstName?: string;
      fullName?: string;
      lastName?: string;
      propertyTaxes?: string;
      state?: NetSheetStateEnum;
      zip?: string;
    };
  }>()
);

export const loadPropertyProfilePrefillInformationFailure = createAction(
  '[Net Sheet] Load Property Profile Prefill Information Failure',
  props<{
    payload: {
      error: HttpErrorResponse;
    };
  }>()
);

export const prefillInformation = createAction(
  '[Net Sheet] Prefill Information',
  props<{
    payload: {
      address?: string;
      city?: string;
      county?: string;
      firstName?: string;
      fullName?: string;
      lastName?: string;
      propertyTaxes?: string;
      state?: NetSheetStateEnum;
      zip?: string;
    };
  }>()
);

export const resetFields = createAction('[Net Sheet] Reset Fields');

export const updateCustomerInformation = createAction(
  '[Net Sheet] Update Customer Information',
  props<{
    payload: Partial<NetSheetCustomerInformation>;
  }>()
);

export const updateTransactionInformationLocation = createAction(
  '[Net Sheet] Update Transaction Information Location',
  props<{
    payload: Partial<{
      transactionInformationEscrowBranch: string | null;
      transactionInformationEscrowCity: string | null;
      transactionInformationEscrowCounty: string | null;
      transactionInformationEscrowState: NetSheetStateEnum | null;
      transactionInformationPropertyCity: string | null;
      transactionInformationPropertyCounty: string | null;
      transactionInformationPropertyState: NetSheetStateEnum | null;
      transactionInformationPropertyZip: string | null;
    }>;
  }>()
);

export const updateTransactionInformation = createAction(
  '[Net Sheet] Update Transaction Information',
  props<{
    payload: Partial<{
      transactionInformationBrokerageFee: string | null;
      transactionInformationBuyerCreditAmount: string | null;
      transactionInformationCityTransferTaxSplit: NetSheetSplitOptionsEnum | null;
      transactionInformationClosingDate: string | null;
      transactionInformationCountyTransferTaxSplit: NetSheetSplitOptionsEnum | null;
      transactionInformationEscrowFeeSplit: NetSheetSplitOptionsEnum | null;
      transactionInformationFirpta: boolean | null;
      transactionInformationFtb: boolean | null;
      transactionInformationHarpta: boolean | null;
      transactionInformationHazardInsuranceAmount: string | null;
      transactionInformationLoanPrimaryAmount: string | null;
      transactionInformationLoanPrimaryInterestRate: string | null;
      transactionInformationLoanPrimaryOriginationFees: string | null;
      transactionInformationMortgagePayoffCount: number | null;
      transactionInformationNaturalHazardDisclosure: string | null;
      transactionInformationNewLoan: boolean | null;
      transactionInformationOwnersExemption: boolean | null;
      transactionInformationOwnersPolicySplit: NetSheetSplitOptionsEnum | null;
      transactionInformationPersonalResidence: boolean | null;
      transactionInformationPropertyAddress: string | null;
      transactionInformationPropertyIncorporatedArea: boolean | null;
      transactionInformationReo: boolean | null;
      transactionInformationSalesPrice: string | null;
      transactionInformationSewerBill: string | null;
      transactionInformationShortSale: boolean | null;
      transactionInformationSubEscrow: boolean | null;
      transactionInformationSurveyAmendmentSplit: boolean | null;
      transactionInformationWarrantyPremiumAmount: string | null;
      transactionInformationWaterBill: string | null;
    }>;
  }>()
);

export const updateMortgage = createAction(
  '[Net Sheet] Update Mortgage',
  props<{
    payload: Partial<NetSheetMortgage>;
  }>()
);

export const updateSalesCommissions = createAction(
  '[Net Sheet] Update Sales Commissions',
  props<{
    payload: Partial<{
      salesCommissionBrokerCommissionListing: string | null;
      salesCommissionBrokerCommissionSelling: string | null;
      salesCommissionBrokerCommissionSplits: boolean;
      salesCommissionBrokerCommissionTaxListing: boolean;
      salesCommissionBrokerCommissionTaxSelling: boolean;
      salesCommissionBrokerCommissionTaxTotal: boolean;
      salesCommissionBrokerCommissionTierRate: string | null;
      salesCommissionBrokerCommissionTotal: string | null;
    }>;
  }>()
);

export const updateTaxes = createAction(
  '[Net Sheet] Update Taxes',
  props<{
    payload: Partial<{
      taxes: string | null;
      taxesHomeownersDuesAmount: string | null;
      taxesHomeownersDuesDueDate: string | null;
      taxesHomeownersDuesPaid: boolean;
      taxesHomeownersDuesTerm: NetSheetTaxesHomeownersDuesTerm | null;
      taxesNextInstallment: string | null;
      taxesPaid: boolean;
      taxesPaidToDateDropdown: string | null;
      taxesPaidToDatePicker: string | null;
      taxesTerm: NetSheetTaxesTerm | null;
    }>;
  }>()
);

export const updateMiscItems = createAction(
  '[Net Sheet] Update Misc Items',
  props<{
    payload: {
      miscItems: NetSheetFormMiscItem[];
    };
  }>()
);

export const updateUserInformation = createAction(
  '[Net Sheet] Update User Information',
  props<{
    payload: Partial<{
      userInformationCompany: string | null;
      userInformationEmail: string | null;
      userInformationFirstName: string | null;
      userInformationLastName: string | null;
      userInformationPhone: string | null;
    }>;
  }>()
);

export const calculate = createAction('[Net Sheet] Calculate');

export const calculateSuccess = createAction(
  '[Net Sheet] Calculator Success',
  props<{
    payload: {
      calculatorReport: NetSheetResult | null;
    };
  }>()
);

export const calculateFailure = createAction(
  '[Net Sheet] Calculate Failure',
  props<{
    payload: {
      error: HttpErrorResponse;
    };
  }>()
);

export const emailExportPdf = createAction(
  '[Net Sheet] Email Export Pdf',
  props<{
    payload: {
      email: string;
    };
  }>()
);

export const emailExportPdfFailure = createAction(
  '[Net Sheet] Email Export Pdf Failure',
  props<{
    payload: {
      error: HttpErrorResponse;
    };
  }>()
);
